import AccordionCustom from "@components/Accordion/AccordionCustom";
import ImageGrid from "@components/ImageGrid";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import OdooHeader from "@components/LandingPageComponents/OdooHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import dynamic from "next/dynamic";
import Head from "next/head";
import goireland from "public/goireland.png";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/webengage/section-1.png",
        title: "Incite Your Customers’ Wanderlust",
        description: "Send a series of automated nudges to your prospects and customers to keep them up-to-date and excited about their upcoming travel plans.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/webengage/section-2.png",
        title: "Skyrocket Your E-Commerce Sales",
        description: "Say goodbye to common e-commerce challenges and increase your e-commerce conversions with an AI-assisted WhatsApp shopping bot.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/webengage/section-3.png",
        title: "Build a Successful Ed-Tech Business",
        description: "Lack of student-teacher engagement will be a thing of the past once you integrate WebEngage with Gallabox’s WhatsApp Automation platform.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/webengage/section-4.png",
        title: "Focus on Your Care Delivery Experience",
        description: "Ensure that your patients receive the best possible care with 24/7 healthcare support and timely reminders while improving patient experience.",
    },
];

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "How do I integrate WebEngage with WhatsApp?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "You can connect WhatsApp and WebEngage using data integration platforms like Zapier or dedicated WhatsApp Automation Tools like Gallabox. While using a connecting platform would require a lot of manual time and effort, investing in an AI-powered WhatsApp Chatbot like Gallabox makes the process easy with its host of pre-built native third-party integrations.",
            },
        },
        {
            "@type": "Question",
            name: "Why is WebEngage Integration with WhatsApp Business API Important?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Listed below are reasons why a seamless integration between WebEngage and your WhatsApp Business API must be an integral part of your customer engagement strategy:1.WhatsApp is a widely used platform with more than 2 billion active users,2. prefer business interactions that are interactive and personalised ,3.Improves brand recognition and enables businesses to build lasting relationships",
            },
        },
        {
            "@type": "Question",
            name: "How Does WhatsApp and WebEngage Integration Help Improve Retention?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Integrating your WebEngage account with Gallabox is swift and effortless. Here’s how you configure Gallabox as a WhatsApp Service Provider from your WebEngage Dashboard:1.Log into your WebEngage account,2.Navigate to the Data Platform page using the Navigation bar,3.Select Integrations from the dropdown and select WhatsApp Setup,4.Click on Configure and add Gallabox as a Private WSP,5.Enter the API key(generated in Gallabox) and other details to complete the configuration",
            },
        },
    ],
};

const webengage: React.FC = () => {
    return (
        <NewLayout
            title="WebEngage WhatsApp Integration | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/webengage/og.png" }}
            canonical="https://gallabox.com/whatsapp-integrations/webengage"
            description="Automate customer journeys with WebEngage WhatsApp integration. Send personalized WhatsApp campaigns effortlessly."
        >
            <Head>
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }} />
            </Head>
            <div className="bg-white">
                <OdooHeader
                    pageTitle="WhatsApp WebEngage Integration"
                    mainTitle="WebEngage WhatsApp Integration for Effective Customer Engagement"
                    description="Enable all your marketing channels to work in unison and improve customer retention and loyalty with AI-powered conversations."
                    buttonText="Sign Up Today"
                    imageUrl="https://assets.gallabox.com/gb-home/webengage/header.png"
                />

                <div className="lg:max-w-7xl mx-auto">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/webengage/card-1.png"
                    title="Keep Customers Engaged in Their Most Preferred Channel"
                    description="Today customers want to communicate with businesses the same way they communicate with friends and family: through informal, secure chats in a channel they prefer. The integration of Gallabox and Webengage allows you to provide this level of comfort to your customers."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/webengage/card-2.png"
                    title="Convert Leads to Deals with WebEngage + Gallabox"
                    description="Make your marketing campaigns stand out with bulk broadcasts, WhatsApp Drip Marketing, and Click-to-WhatsApp Ads. Don’t lose the interest of your potential leads, convert them quicker with ChatGPT-powered replies and analyse the performance of WhatsApp as a marketing channel."
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/webengage/card-3.png"
                    title="Make Personalisation Your Key Brand Differentiator"
                    description="Shooting the same message template to all your contacts will not help improve your engagement rates. Segment your contacts based on their specific behaviour and engage them more effectively with tailor-made responses and message templates."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/webengage/card-4.png"
                    title="Build a Unified Tech Stack with WebEngage-WhatsApp Integration"
                    description="Are you tired of switching tabs and manually collating data to understand your campaign performance? Now, you can integrate your WebEngage account with your WhatsApp business API to eliminate manual efforts and implement an effective marketing attribution strategy."
                />

                <YellowCTAhref
                    buttonText="Get Started Today"
                    customText={
                        <h3 className="text-2xl md:text-3xl font-bold text-yellow-800">
                            Accelerate Growth with a Seamless <br /> WebEngage and WhatsApp Integration
                        </h3>
                    }
                />

                <TestimonialSection
                    TestimonialSectionPropsData={[
                        {
                            companyImg: goireland,
                            headerTitle: "Gallabox’s WhatsApp Widget reduced FTR  from 15-30 minutes to less than a minute, boosting engagement and conversion rates.",
                            avatarImg: "https://assets.gallabox.com/gb-home/go-ireland/goireland-owner.png",
                            nameText: "Ilaya Bharathi",
                            professionText: "Founder & CEO, GoIreland",
                        },
                    ]}
                />

                <ImageGrid />

                <section className="max-w-6xl py-4 mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                        <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about WebEngage-Gallabox integration</h3>
                    </div>
                    <AccordionCustom
                        title="WebEngage-Gallabox integration"
                        items={[
                            {
                                question: "How do I integrate WebEngage with WhatsApp?",
                                answer: "You can connect WhatsApp and WebEngage using data integration platforms like Zapier or dedicated WhatsApp Automation Tools like Gallabox. While using a connecting platform would require a lot of manual time and effort, investing in an AI-powered WhatsApp Chatbot like Gallabox makes the process easy with its host of pre-built native third-party integrations.",
                            },
                            {
                                question: "Why is WebEngage Integration with WhatsApp Business API Important?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "Listed below are reasons why a seamless integration between WebEngage and your WhatsApp Business API must be an integral part of your customer engagement strategy:"
                                            }
                                        </span>

                                        <ol className="pl-1 pt-2">
                                            <li>{"WhatsApp is a widely used platform with more than 2 billion active users."}</li>
                                            <li>{"Customers prefer business interactions that are interactive and personalised "}</li>
                                            <li>{"Improves brand recognition and enables businesses to build lasting relationships"}</li>
                                        </ol>
                                    </>
                                ),
                            },
                            {
                                question: "How Does WhatsApp and WebEngage Integration Help Improve Retention?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "Integrating your WebEngage account with Gallabox is swift and effortless. Here’s how you configure Gallabox as a WhatsApp Service Provider from your WebEngage Dashboard:"
                                            }
                                        </span>
                                        <ol className="pl-1 pt-2">
                                            <li>{"Log into your WebEngage account."}</li>
                                            <li>{"Navigate to the Data Platform page using the Navigation bar."}</li>
                                            <li>{"Select Integrations from the dropdown and select WhatsApp Setup."}</li>
                                            <li>{"Click on Configure and add Gallabox as a Private WSP."}</li>
                                            <li>{"Enter the API key (generated in Gallabox) and other details to complete the configuration."}</li>
                                        </ol>
                                    </>
                                ),
                            },
                        ]}
                    />
                </section>
            </div>
        </NewLayout>
    );
};

export default webengage;
